import { sanitizeHtml } from "../client/utils/sanitize";
import { normalizeSymbol } from "../shared/utils";
import { useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Clock, TrendingUp, TrendingDown, Pencil, LucideYoutube, Share2 } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Card, CardContent } from "../client/components/ui/card";
import { SessionAccountsTable } from "./SessionAccountsTable";
import {
  getMoodEmoji,
  getMoodLabel,
  getPerformanceLabel,
  getPerformanceColor,
  extractYouTubeId,
  sessionChartWindow,
} from "./sessionUtils";
import { SessionErrorSelect } from "./SessionErrorSelect";
import { SessionErrorBadges } from "./SessionErrorBadges";
import NotebookEditor from "../notebook/LazyNotebookEditor";
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";
import { motion } from "framer-motion";
import { SessionChart } from "./SessionChart";
import { useSessionDetail, parisOffsetLabel } from "./useSessionDetail";

export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  // Toute la logique vit dans useSessionDetail, partagée avec le mode Lite.
  const {
    t, navigate, isDark,
    session, isLoading, accountsData,
    sessionTrades, sessionPnl, pnlByAccount,
    mood, performance, errorIds, mentalState, setMentalState,
    htmlNotes, setHtmlNotes, handleSaveNotes,
    maxRunup, setMaxRunup, maxDrawdown, setMaxDrawdown,
    startedAt, setStartedAt, endedAt, setEndedAt,
    confirmDelete, setConfirmDelete, handleDelete,
    sessionName, setSessionName, handleSaveName,
    setIsShareDialogOpen,
    videoUrl, videoInput, setVideoInput, videoInputError, setVideoInputError,
    isEditingVideo, setIsEditingVideo, handleSaveVideo, handleDeleteVideo,
    handleImageUpload, handleResolveImageKeys,
    handleStartedAtBlur, handleEndedAtBlur,
    handleMoodChange, handlePerformanceChange, handleErrorsChange,
    handleMentalStateBlur, handleMaxRunupBlur, handleMaxDrawdownBlur,
    sessionDate, actualDurationLabel, plannedDurationLabel, metricsAnimKey,
    dialogs,
  } = useSessionDetail(sessionId);

  const inlineInputClass =
    "w-full bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none text-sm py-1 transition-colors placeholder:text-muted-foreground/40";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground mb-4">{t.sessionDetail.notFound}</p>
        <Button variant="outline" onClick={() => navigate("/trading-sessions")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.sessionDetail.back}
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full py-10">
      <div className="space-y-6">
        {/* Header row: back + delete */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/trading-sessions")}
            className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.sessionDetail.back}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setIsShareDialogOpen(true)}
            >
              <Share2 className="w-4 h-4" />
              {t.sessionDetail.shareButton}
            </Button>

            {confirmDelete ? (
              <>
                <span className="text-sm text-muted-foreground">{t.sessionDetail.deleteConfirm}</span>
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  {t.sessionDetail.delete}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>✕</Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="w-4 h-4" />
                {t.sessionDetail.delete}
              </Button>
            )}
          </div>
        </div>

        {/* Hero header */}
        <div className="space-y-3">
          {/* Title + date on the same line */}
          <div className="flex items-baseline gap-4 flex-wrap">
            {/* Inline editable session name */}
            <div className="relative group flex-1 min-w-0">
              <input
                type="text"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value.slice(0, 100))}
                onBlur={() => handleSaveName(sessionName)}
                placeholder={t.sessionDetail.namePlaceholder}
                maxLength={100}
                className="w-full bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none text-3xl font-bold py-1 transition-colors placeholder:text-muted-foreground/30 placeholder:font-normal placeholder:text-xl"
                style={{ fontFamily: "'Red Hat Display', sans-serif" }}
              />
              {sessionName.length > 0 && (
                <span className="absolute right-0 bottom-2 text-[10px] text-muted-foreground/40 group-focus-within:opacity-100 opacity-0 transition-opacity">
                  {sessionName.length}/100
                </span>
              )}
            </div>
            <span
              className="text-3xl font-bold text-muted-foreground/50 capitalize shrink-0"
              style={{ fontFamily: "'Red Hat Display', sans-serif" }}
            >
              {sessionDate}
            </span>
          </div>
          <motion.div
            key={metricsAnimKey}
            variants={metricsContainerVariants}
            initial="hidden"
            animate="show"
            className="flex items-start justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-4 flex-wrap">
              {/* Mood badge */}
              {mood && (
                <motion.div variants={metricsItemVariants} className="flex items-center gap-2 bg-muted/50 rounded-xl px-4 py-2">
                  <span className="text-2xl">{getMoodEmoji(mood)}</span>
                  <span className="text-sm font-medium">{getMoodLabel(mood, t)}</span>
                </motion.div>
              )}
              {/* Performance badge */}
              {performance && (
                <motion.div variants={metricsItemVariants} className={`rounded-xl px-4 py-2 text-sm font-semibold bg-muted/50 ${getPerformanceColor(performance)}`}>
                  {getPerformanceLabel(performance, t)}
                </motion.div>
              )}
              {/* Error badges */}
              {errorIds.length > 0 && (
                <SessionErrorBadges
                  errors={((session as any)?.errors ?? []).filter(
                    (e: { id: string }) => errorIds.includes(e.id),
                  )}
                />
              )}
              {/* Duration badge */}
              <motion.div variants={metricsItemVariants} className="flex items-center gap-2 text-muted-foreground text-sm">
                <Clock className="w-4 h-4" />
                <span>{actualDurationLabel}</span>
              </motion.div>
            </div>

            {/* P&L hero */}
            {sessionPnl !== null && (
              <motion.div variants={metricsItemVariants}>
                <AnimatedMetric
                  value={sessionPnl}
                  format="currency"
                  prefix={sessionPnl >= 0 ? "+" : undefined}
                  suffix=" $"
                  className={`text-3xl font-bold tabular-nums ${sessionPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}
                />
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Main layout: large content + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 items-start">

          {/* LEFT — rich text editors */}
          <div className="space-y-6">
            {/* Video Card */}
            <Card className="overflow-hidden">
              <div className="px-5 pt-4 pb-2 border-b border-border/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LucideYoutube className="w-4 h-4 text-primary/70" />
                  <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                    {t.sessionDetail.videoTitle}
                  </p>
                </div>
                {videoUrl && !isEditingVideo && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => { setVideoInput(videoUrl); setIsEditingVideo(true); setVideoInputError(""); }}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title={t.sessionDetail.videoEdit}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteVideo}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
                      title={t.sessionDetail.videoDelete}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <CardContent className="p-0">
                {/* Player state */}
                {videoUrl && !isEditingVideo && (() => {
                  const videoId = extractYouTubeId(videoUrl);
                  return videoId ? (
                    <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                        title={t.sessionDetail.videoTitle}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : null;
                })()}

                {/* Empty state or Edit state */}
                {(!videoUrl || isEditingVideo) && (
                  <div className="p-5 space-y-3">
                    {!isEditingVideo && (
                      <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground">
                        <LucideYoutube className="w-8 h-8 opacity-30" />
                        <p className="text-sm text-center">{t.sessionDetail.videoInvite}</p>
                      </div>
                    )}
                    <input
                      type="url"
                      value={videoInput}
                      onChange={(e) => { setVideoInput(e.target.value); setVideoInputError(""); }}
                      placeholder={t.sessionDetail.videoPlaceholder}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    {videoInputError && (
                      <p className="text-xs text-destructive">{videoInputError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveVideo}
                        className="px-4 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                      >
                        {t.sessionDetail.videoSave}
                      </button>
                      {isEditingVideo && (
                        <button
                          type="button"
                          onClick={() => { setIsEditingVideo(false); setVideoInputError(""); }}
                          className="px-4 py-1.5 rounded-md border border-input text-sm hover:bg-muted transition-colors"
                        >
                          {t.sessionDetail.videoCancel}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <SessionChart
              session={sessionChartWindow(session ?? { startedAt: null, endedAt: null }, sessionTrades)}
              trades={sessionTrades}
              accounts={(accountsData as any)?.accounts ?? []}
              isDark={isDark}
            />

            {/* Accounts active during this session */}
            <SessionAccountsTable
              accounts={(((accountsData as any)?.accounts) ?? []) as any[]}
              pnlByAccount={pnlByAccount}
              t={t}
              onAccountClick={(id) => navigate(`/account/${id}`)}
            />

            {/* General Notes — full width, first */}
            <Card className="overflow-hidden">
              <div className="px-5 pt-4 pb-2 border-b border-border/50">
                <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                  {t.sessionDetail.notes}
                </p>
              </div>
              <CardContent className="p-0">
                <div
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      handleSaveNotes();
                    }
                  }}
                >
                  <NotebookEditor
                    content={htmlNotes}
                    onChange={(html) => {
                      setHtmlNotes(html);
                    }}
                    placeholder={t.session.notesPlaceholder}
                    onImageUpload={handleImageUpload}
                    resolveImageKeys={handleResolveImageKeys}
                  />
                </div>
              </CardContent>
            </Card>


            {/* Notes du tuteur — affichées uniquement si au moins une existe */}
            {(session as any)?.mentorNotes?.length > 0 && (
              (session as any).mentorNotes.map((note: any) => (
                <Card key={note.id} className="overflow-hidden border-primary/30">
                  <div className="px-5 pt-4 pb-2 border-b border-primary/20 bg-primary/5">
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-primary">
                      {t.mentor.mentorNotes}
                    </p>
                  </div>
                  <CardContent className="p-5">
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none text-sm text-foreground"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.content ?? "") }}
                    />
                  </CardContent>
                </Card>
              ))
            )}

            {/* Trades de la session */}
            <Card className="overflow-hidden">
              <div className="px-5 pt-4 pb-3 border-b border-border/50">
                <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
                  {t.sessionDetail.trades}
                </p>
              </div>
              <CardContent className="p-4">
                {(() => {
                  if (sessionTrades.length === 0) {
                    return <p className="text-sm text-muted-foreground">{t.sessionDetail.noTrades}</p>;
                  }
                  return (
                    <div className="space-y-2">
                      {sessionTrades.map((trade: any) => (
                        <div
                          key={trade.id}
                          onClick={() => navigate(`/trade/${trade.id}`)}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 text-sm cursor-pointer hover:bg-accent/40 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                trade.type === "long"
                                  ? "bg-emerald-500/10 text-emerald-500"
                                  : "bg-red-500/10 text-red-500"
                              }`}
                            >
                              {trade.type === "long" ? t.journal.long : t.journal.short}
                            </span>
                            <span className="font-mono font-semibold">{normalizeSymbol(trade.symbol)}</span>
                            <span className="text-muted-foreground text-xs">
                              {new Date(trade.date).toLocaleDateString()}
                            </span>
                          </div>
                          {trade.pnl !== null && (
                            <span
                              className={`font-mono font-semibold tabular-nums text-xs ${
                                trade.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"
                              }`}
                            >
                              {trade.pnl >= 0 ? "+" : ""}
                              {trade.pnl.toFixed(2)} $
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — sidebar */}
          <div className="space-y-4">

            {/* Timing */}
            <Card>
              <div className="px-4 pt-4 pb-2">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                  {t.sessionDetail.timing}
                </p>
              </div>
              <CardContent className="px-4 pb-4 space-y-3">
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.startedAt}</p>
                  <input
                    type="datetime-local"
                    value={startedAt}
                    onChange={(e) => setStartedAt(e.target.value)}
                    onBlur={handleStartedAtBlur}
                    className={inlineInputClass}
                  />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.endedAt}</p>
                  <input
                    type="datetime-local"
                    value={endedAt}
                    onChange={(e) => setEndedAt(e.target.value)}
                    onBlur={handleEndedAtBlur}
                    className={inlineInputClass}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {parisOffsetLabel()}
                </p>
                <div className="flex gap-6 pt-1">
                  <div>
                    <p className="text-[11px] text-muted-foreground">{t.sessionDetail.duration}</p>
                    <p className="text-sm font-semibold mt-0.5">{actualDurationLabel}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">{t.sessionDetail.planned}</p>
                    <p className="text-sm font-semibold mt-0.5">{plannedDurationLabel}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Evaluation */}
            <Card>
              <div className="px-4 pt-4 pb-2">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                  {t.sessionDetail.evaluation}
                </p>
              </div>
              <CardContent className="px-4 pb-4 space-y-3">
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.session.mood}</p>
                  <select
                    value={mood}
                    onChange={(e) => handleMoodChange(e.target.value)}
                    className="w-full bg-transparent border-0 border-b border-border text-sm py-1 focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">—</option>
                    <option value="excellent">{getMoodEmoji("excellent")} {t.session.moodExcellent}</option>
                    <option value="good">{getMoodEmoji("good")} {t.session.moodGood}</option>
                    <option value="neutral">{getMoodEmoji("neutral")} {t.session.moodNeutral}</option>
                    <option value="poor">{getMoodEmoji("poor")} {t.session.moodPoor}</option>
                    <option value="bad">{getMoodEmoji("bad")} {t.session.moodBad}</option>
                  </select>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.session.performance}</p>
                  <select
                    value={performance}
                    onChange={(e) => handlePerformanceChange(e.target.value)}
                    className="w-full bg-transparent border-0 border-b border-border text-sm py-1 focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">—</option>
                    <option value="excellent">{t.analytics.excellent}</option>
                    <option value="good">{t.analytics.good}</option>
                    <option value="average">{t.analytics.average}</option>
                    <option value="poor">{t.analytics.poor}</option>
                  </select>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.session.errors}</p>
                  <SessionErrorSelect
                    value={errorIds}
                    onChange={handleErrorsChange}
                  />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">{t.sessionDetail.mentalState}</p>
                  <input
                    type="text"
                    value={mentalState}
                    onChange={(e) => setMentalState(e.target.value)}
                    onBlur={handleMentalStateBlur}
                    placeholder="—"
                    className={inlineInputClass}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Metrics */}
            <Card>
              <div className="px-4 pt-4 pb-2">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                  {t.sessionDetail.metrics}
                </p>
              </div>
              <CardContent className="px-4 pb-4 space-y-3">
                {/* P&L */}
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <p className="text-[11px] text-muted-foreground">{t.sessionDetail.pnl}</p>
                  <p className={`text-base font-bold tabular-nums ${
                    sessionPnl === null ? "text-muted-foreground" : sessionPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"
                  }`}>
                    {sessionPnl === null ? t.sessionDetail.noValue : `${sessionPnl >= 0 ? "+" : ""}${sessionPnl.toFixed(2)} $`}
                  </p>
                </div>

                {/* P&L par compte */}
                {pnlByAccount.length > 0 && (
                  <div className="py-2 border-b border-border/50 space-y-1.5">
                    <p className="text-[11px] text-muted-foreground">{t.sessionDetail.pnlByAccount}</p>
                    <div className="space-y-1">
                      {pnlByAccount.map((row) => (
                        <div key={row.accountId ?? "none"} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-foreground/80 truncate">
                            {row.name ?? t.sessionDetail.noAccount}
                          </span>
                          <span className={`text-xs font-semibold font-mono tabular-nums ${
                            row.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"
                          }`}>
                            {row.pnl >= 0 ? "+" : ""}{row.pnl.toFixed(2)} $
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Max Run-up */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3 h-3 text-pnlPositive" />
                    <p className="text-[11px] text-muted-foreground">{t.sessionDetail.maxRunup}</p>
                  </div>
                  <input
                    type="number"
                    value={maxRunup}
                    onChange={(e) => setMaxRunup(e.target.value)}
                    onBlur={handleMaxRunupBlur}
                    placeholder="—"
                    className={inlineInputClass}
                  />
                </div>

                {/* Max Drawdown */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingDown className="w-3 h-3 text-pnlNegative" />
                    <p className="text-[11px] text-muted-foreground">{t.sessionDetail.maxDrawdown}</p>
                  </div>
                  <input
                    type="number"
                    value={maxDrawdown}
                    onChange={(e) => setMaxDrawdown(e.target.value)}
                    onBlur={handleMaxDrawdownBlur}
                    placeholder="—"
                    className={inlineInputClass}
                  />
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      {dialogs}
    </div>
  );
}
